import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { AppProvider, useAppContext } from './context/AppContext';
import { TbkConnectionProvider } from './context/TbkConnectionContext';
import { XSignConnectionProvider } from './context/XSignConnectionContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios from 'axios';
import TokenLogin from './components/auth/TokenLogin';
import MainLayout from './components/layout/MainLayout';
import CategorySelectionScreen from './components/categories/CategorySelectionScreen';
import SubcategorySelectionScreen from './components/categories/SubcategorySelectionScreen';
import ProductList from './components/products/ProductList';
import PaymentScreen from './components/checkout/PaymentScreen';
import PaymentMethodScreen from './components/checkout/PaymentMethodScreen';
import OperateCardScreen from './components/checkout/OperateCardScreen';
import ThankYouScreen from './components/checkout/ThankYouScreen';
import WelcomeScreen from './components/welcome/WelcomeScreen';
import useInactivityTimer from './hooks/useInactivityTimer';
import { useCart } from './context/CartContext';
import { CombosProvider } from './context/CombosContext';
import connectionService from './services/ConnectionService';
import OfflineService from './services/OfflineService';
import './index.css';

const queryClient = new QueryClient();

const PAYMENT_PATHS = ['/payment', '/payment-method', '/operate-card', '/thank-you'];

// Componente aislado para el modal de inactividad — el countdown vive aquí
// y no re-renderiza el resto de la app cada segundo
const InactivityWarningModal = ({ onContinue, onExpire }) => {
  const [countdown, setCountdown] = useState(20);
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          onExpire();
          return 20;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
    }}>
      <div style={{
        background: '#fff', borderRadius: '1.5rem', padding: '3rem',
        maxWidth: '520px', width: '90%', textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⏱️</div>
        <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#1a1a1a', marginBottom: '0.75rem' }}>
          ¿Sigues ahí?
        </h2>
        <p style={{ fontSize: '1.3rem', color: '#555', marginBottom: '0.5rem' }}>
          Tu orden será borrada en
        </p>
        <div style={{
          fontSize: '5rem', fontWeight: 900,
          color: countdown <= 5 ? '#e53935' : '#0E8164',
          lineHeight: 1, margin: '0.5rem 0 1.5rem'
        }}>
          {countdown}
        </div>
        <p style={{ fontSize: '1.1rem', color: '#888', marginBottom: '2rem' }}>segundos</p>
        <button
          onClick={onContinue}
          style={{
            backgroundColor: '#0E8164', color: '#fff', border: 'none',
            borderRadius: '50px', padding: '1.1rem 3rem',
            fontSize: '1.4rem', fontWeight: 800, cursor: 'pointer', width: '100%'
          }}
        >
          Continuar con mi orden
        </button>
      </div>
    </div>
  );
};

const AppRoutes = ({ isAuthenticated, setIsAuthenticated }) => {
  const { clearSessionData, sessionData, settings } = useAppContext();
  const { items, clearCart } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [showWelcome, setShowWelcome] = useState(false);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);

  const { isInactive, resetTimer, stopTimer, startTimer } = useInactivityTimer(60000);

  const isPaymentScreen = PAYMENT_PATHS.includes(location.pathname);

  useEffect(() => {
    if (isAuthenticated && location.pathname !== '/login') {
      if (isPaymentScreen) {
        stopTimer();
      } else {
        startTimer();
      }
    } else {
      stopTimer();
    }
  }, [isAuthenticated, location.pathname, startTimer, stopTimer, isPaymentScreen]);

  // Cuando el timer de inactividad se dispara
  useEffect(() => {
    if (isInactive && isAuthenticated && !showWelcome && !isPaymentScreen) {
      if (items.length > 0) {
        setShowInactivityWarning(true);
      } else {
        setShowWelcome(true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInactive]);

  const handleContinueOrder = () => {
    setShowInactivityWarning(false);
    resetTimer();
  };

  const handleInactivityExpire = () => {
    setShowInactivityWarning(false);
    clearCart();
    setShowWelcome(true);
  };

  const handleLogin = () => {
    setIsAuthenticated(true);
    resetTimer();
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    clearSessionData();
    clearCart();
    setShowWelcome(false);
    setShowInactivityWarning(false);
    stopTimer();
  };

  const handleWelcomeStart = () => {
    setShowWelcome(false);
    resetTimer();
    navigate('/');
  };

  // Ref para saber si estamos en pantalla de pago sin recrear el interval
  const isPaymentScreenRef = useRef(false);
  useEffect(() => {
    isPaymentScreenRef.current = isPaymentScreen;
  }, [isPaymentScreen]);

  // Polling: verifica cada 10s si la sesión sigue abierta en Odoo
  useEffect(() => {
    if (!isAuthenticated || !sessionData?.session_id) return;

    const sessionId = sessionData.session_id;
    const apiUrl = settings?.apiUrl || 'http://localhost:9000';

    const interval = setInterval(async () => {
      // No interrumpir mientras se procesa un pago
      if (isPaymentScreenRef.current) return;

      try {
        const res = await axios.get(`${apiUrl}/api/pos/session-state`, {
          params: { session_id: sessionId },
          timeout: 8000
        });

        if (res.data?.is_open === false) {
          console.warn('[SESSION] Sesión cerrada en Odoo — cerrando totem');
          OfflineService.clearSessionInfo();
          handleLogout();
        }
      } catch (e) {
        console.warn('[SESSION] Error verificando sesión:', e.message);
      }
    }, 10000);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, sessionData?.session_id]);

  const ProtectedRoute = ({ children }) => {
    return isAuthenticated ? children : <Navigate to="/login" />;
  };

  if (showWelcome) {
    return <WelcomeScreen onStart={handleWelcomeStart} />;
  }

  return (
    <>
      {showInactivityWarning && (
        <InactivityWarningModal
          onContinue={handleContinueOrder}
          onExpire={handleInactivityExpire}
        />
      )}
      <Routes>
        <Route path="/login" element={<TokenLogin onLogin={handleLogin} />} />
        <Route path="/welcome" element={<WelcomeScreen />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout onLogout={handleLogout}>
              <CategorySelectionScreen />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subcategories/:categoryId"
        element={
          <ProtectedRoute>
            <MainLayout onLogout={handleLogout}>
              <SubcategorySelectionScreen />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:categoryId"
        element={
          <ProtectedRoute>
            <MainLayout onLogout={handleLogout}>
              <ProductList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:categoryId/:subcategoryId"
        element={
          <ProtectedRoute>
            <MainLayout onLogout={handleLogout}>
              <ProductList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment"
        element={
          <ProtectedRoute>
            <MainLayout onLogout={handleLogout}>
              <PaymentScreen />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-method"
        element={
          <ProtectedRoute>
            <MainLayout onLogout={handleLogout}>
              <PaymentMethodScreen />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/operate-card"
        element={
          <ProtectedRoute>
            <MainLayout onLogout={handleLogout}>
              <OperateCardScreen />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/thank-you"
        element={
          <ProtectedRoute>
            <MainLayout onLogout={handleLogout}>
              <ThankYouScreen />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isOnline, setIsOnline] = useState(connectionService.getConnectionStatus());

  useEffect(() => {
    const block = (e) => e.preventDefault();
    document.addEventListener('contextmenu', block);
    // Bloquear long-press en Android que dispara menú de imagen/link
    document.addEventListener('touchstart', (e) => {
      if (e.target.tagName === 'IMG' || e.target.tagName === 'A') {
        e.preventDefault();
      }
    }, { passive: false });
    return () => document.removeEventListener('contextmenu', block);
  }, []);

  useEffect(() => {
    console.log('App: Inicializando ConnectionService...');

    const unsubscribe = connectionService.subscribe((status) => {
      console.log('App: Cambio de conexión detectado:', status ? 'Conectado' : 'Desconectado');
      setIsOnline(status);
    });

    return () => {
      console.log('App: Limpiando suscripción a ConnectionService');
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    console.log('App: Verificando conectividad inicial...');
    connectionService.forceCheck()
      .then((status) => {
        console.log('App: Verificación inicial completada, estado:', status ? 'Conectado' : 'Desconectado');
      })
      .catch((error) => {
        console.error('App: Error en verificación inicial:', error);
      });
  }, []);

  useEffect(() => {
    if (isOnline) {
      console.log('App: Sistema conectado - Las ventas se enviarán a la API intermedia en tiempo real');
    } else {
      console.log('App: Sistema desconectado - La API intermedia se encargará de sincronizar automáticamente cuando se restablezca la conexión');
    }
  }, [isOnline]);

  return (
    <QueryClientProvider client={queryClient}>
      <AppProvider>
        <TbkConnectionProvider>
          <XSignConnectionProvider>
            <CartProvider>
              <CombosProvider>
              <Router>
                <AppRoutes
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                />
              </Router>
              </CombosProvider>
            </CartProvider>
          </XSignConnectionProvider>
        </TbkConnectionProvider>
      </AppProvider>
    </QueryClientProvider>
  );
}

export default App;